import * as types from '@/store/mutation-types'
import { saveAs } from 'file-saver'
import { Repository } from '@/repository/repository'
import { stripPayload } from '@/schemas'
import { services } from '@/services'
import config from '../../../config'

const apiRepository = Repository.call('orders')
const ACTION_AWAITS_DISPATCH = 'AWAITS_DISPATCH'
const ACTION_CANCELED = 'CANCELED'
const PARTNER_CTT = 'CTT'

const getActionType = type => (typeof type === 'string' ? type : type.name)
const isCttOrder = order => order.partner && order.partner.toUpperCase().trim() === PARTNER_CTT
const shouldUseCttDispatchAction = (orders, actionType) => actionType === ACTION_AWAITS_DISPATCH && orders.length === 1 && isCttOrder(orders[0])
const getStoreCode = order => order.store && order.store.storeCode
const sanitizePayloadValue = value => {
  if (typeof value !== 'string') return value

  const normalizedValue = value.trim()
  return normalizedValue === '' ? null : normalizedValue
}
const buildCarrierDispatchPayload = order => {
  const customerOrderCode = sanitizePayloadValue(order.customerOrderCode)
  const storeCode = sanitizePayloadValue(getStoreCode(order))

  const payload = {}

  if (customerOrderCode !== undefined && customerOrderCode !== null) payload.customerOrderCode = customerOrderCode
  if (storeCode !== undefined && storeCode !== null) payload.storeCode = storeCode

  return payload
}
const dispatchLegacyOrderFlow = (store, payload) => {
  // ! Changed Endpoints on MOVIMENT-1206, changed status needs another endpoints
  if (payload.type === ACTION_AWAITS_DISPATCH || payload.type === ACTION_CANCELED) return store.dispatch('orders/setSpecialActionsOrder', payload)
  let data = payload.orders.map(order => {
    const dataToSend = {
      status: payload.type || payload.type.name,
      id: order.id
    }

    return dataToSend
  })

  return apiRepository.putEditOrders(data)
}

const state = {
  orders: [],
  availableOrders: [],
  actions: {},
  totalCount: 0
}

const getters = {
  orders: state => state.orders,
  actions: state => state.actions,
  available: state => state.availableOrders,
  totalCount: state => state.totalCount,
  currentPage: state => state.page + 1 || 1
}

const actions = {
  async getOrders ({ commit }, payload) {
    const { filters = null } = payload

    const params = {
      supplierOrderCode: filters.supplierOrderCode || null,
      pyxisDateBegin: filters.pyxisDateBegin || null,
      pyxisDateEnd: filters.pyxisDateEnd || null,
      availabilityDateBegin: filters.availabilityDateBegin || null,
      availabilityDateEnd: filters.availabilityDateEnd || null,
      effectiveStatus: filters.movStateOption || null,
      sort: filters.sort || 'availableDate',
      ord: filters.ord || 'asc',
      page: filters.page - 1 || 0
    }

    const orders = await apiRepository.getCustomerOrders(params)
    const totalCount = parseInt(orders.headers['x-total-count']) || 0
    if (!orders.level) return orders

    const response = services.cleanResponseService(stripPayload(orders.data, 'orders'))
    commit(types.SET_ORDERS_OPEN, {
      response,
      totalCount
    })

    return orders
  },
  async setEditChanges ({ commit }, payload) {
    const {
      palletEquivalentNumber,
      palletEquivalentForPartner,
      palletTotal,
      volume,
      volumeTotal,
      weight
    } = payload.data

    let data = payload.orders.map(order => {
      return {
        status: 'ORDER',
        id: order,
        palletEquivalentNumber,
        palletEquivalentForPartner,
        palletTotal,
        volume,
        volumeTotal,
        weight
      }
    })

    const orders = await apiRepository.putEditOrders(data)
    return orders
  },
  async setActions ({ commit }, payload) {
    const actionType = getActionType(payload.type)

    if (shouldUseCttDispatchAction(payload.orders, actionType)) {
      return this.dispatch('orders/setCttDispatchAction', payload.orders[0])
    }

    return dispatchLegacyOrderFlow(this, payload)
  },
  async setCttDispatchAction ({ commit }, order) {
    if (!isCttOrder(order)) {
      return dispatchLegacyOrderFlow(this, {
        orders: [order],
        type: ACTION_AWAITS_DISPATCH
      })
    }

    const carrierDispatchPayload = buildCarrierDispatchPayload(order)
    const customerOrderCode = carrierDispatchPayload.customerOrderCode
    const storeCode = carrierDispatchPayload.storeCode
    const useCarrierDispatch = customerOrderCode && storeCode && await services.fliptService.isCttDispatchEnabled({
      entityId: customerOrderCode || order.id,
      context: {
        partner: order.partner,
        customerOrderCode,
        storeCode,
        supplierOrderCode: order.supplierOrderCode
      }
    })

    if (useCarrierDispatch) {
      return apiRepository.postCarrierDispatchOrder(carrierDispatchPayload)
    }

    return dispatchLegacyOrderFlow(this, {
      orders: [order],
      type: ACTION_AWAITS_DISPATCH
    })
  },
  async setSpecialActionsRestoreOrder ({ commit }, payload) {
    const data = payload.orderIds.map(id => {
      return {
        effectiveStatus: 'TO_REVERT_SUPPLIER',
        id
      }
    })

    const orders = await apiRepository.putEditOrdersStatus(data)
    return orders
  },
  async setSpecialActionsRejectOrder ({ commit }, payload) {
    const data = payload.orders.map(id => {
      return {
        id,
        reason: payload.data.form.reason,
        description: payload.data.form.comments,
        dateReason: payload.data.dateReason,
        resendOrder: payload.data.form.resendOrder,
        reasonId: payload.data.form.reasonID,
        effectiveStatus: 'TO_REJECT'
      }
    })

    const orders = await apiRepository.putEditOrdersStatus(data)
    return orders
  },

  async setStateInPreparation ({ commit }, {customerOrderCode, store}) {
    const dataToSend = {
      customerOrderCode,
      store
    }
    const changes = await apiRepository.putSetStateInPreparation(dataToSend)
    return changes
  },
  async setSpecialActionsOrder ({ commit }, payload) {
    const data = payload.orders.map(order => {
      return {
        effectiveStatus: payload.type || payload.type.name,
        id: order.id
      }
    })

    const orders = await apiRepository.putEditOrdersStatus(data)
    return orders
  },
  async getAvailableOrders ({ commit }, { filters = null, type }) {
    const params = {
      supplierOrderCode: filters.supplierOrderCode || null,
      effectiveStatus: filters.deliveredEffectiveStatusList || null,
      sort: filters.sort || null,
      ord: filters.ord || 'asc',
      page: filters.page - 1 || 0
    }

    const orders = await apiRepository.getAvailableOrders(params, type)
    const totalCount = parseInt(orders.headers['x-total-count']) || 0
    if (!orders.level) return false

    const response = services.cleanResponseService(stripPayload(orders.data, 'orders'))
    commit(types.SET_AVAILABLE_ORDERS, {
      response,
      totalCount
    })

    return orders
  },
  async downloadCsv (_, { filters }) {
    const params = {
      supplierOrderCode: filters.supplierOrderCode || null,
      orderDateFrom: filters.pyxisDateBegin || null,
      orderDateTo: filters.pyxisDateEnd || null,
      availableDateFrom: filters.availabilityDateBegin || null,
      availableDateTo: filters.availabilityDateEnd || null,
      effectiveStatus: filters.movStateOption || null
    }

    const file = await apiRepository.getDownloadCsv(params)
    if (!file || file.status !== 200) return false
    const timeStamp = Math.floor(Date.now())
    const blob = new Blob([file.data], { type: 'text/csv' })
    saveAs(blob, `movimenta-open-orders-${timeStamp}.csv`)
    return file
  },
  flush ({ commit }) {
    commit(types.SET_FLUSH_ORDERS)
  },
  flushAvailableOrders ({ commit }) {
    commit(types.SET_FLUSH_AVAILABLE_ORDERS)
  }
}

const mutations = {
  [types.SET_ORDERS_OPEN] (state, { response, totalCount = 0 }) {
    state.orders = [ ...response ]
    state.actions = { ...config.data }
    state.totalCount = totalCount
  },
  [types.SET_AVAILABLE_ORDERS] (state, { response, totalCount = 0 }) {
    state.availableOrders = [ ...response ]
    state.totalCount = totalCount
  },
  [types.SET_FLUSH_ORDERS] (state) {
    state.orders = []
  },
  [types.SET_FLUSH_AVAILABLE_ORDERS] (state) {
    state.availableOrders = []
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/modules/orders.js